import { ButtonHTMLAttributes, CSSProperties, ReactNode, RefObject } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import classNames from 'classnames';

import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { EColorGradients } from '@/types/color-gradients.types';
import OptionalComponent from '@ui/optional-component/optional-component';

import './gradient-button.css';

interface IGradientButtonProps {
  gradient?: EColorGradients | string;
  borderWidth?: string;
  borderStyle?: string;
  dashedColor?: string;
  adminButton?: boolean;
  color?: string;
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  opacity?: number;
  hoverOpacity?: number;
  margin?: string | number;
  selected?: boolean;
  children?: ReactNode;
  wrapperClassNames?: string;
  buttonClassNames?: string;
  value?: string;
  alignment?: 'left' | 'center' | 'right';
  loading?: boolean;
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement> & { ref?: RefObject<HTMLButtonElement | null> | null };
  linkProps?: LinkProps;
  fullBackgroundGradient?: boolean;
  asLink?: boolean;
  href?: string;
  inline?: boolean;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  defaultBackground?: string;
  unselectedHoverBackground?: string;
  selectedHoverBackground?: string;
  hoverBoxShadow?: string;
  padding?: string;
  outlineOnFocus?: boolean;
  fillUnselected?: boolean;
  transitionDuration?: CSSProperties['transitionDuration'];
}

/**
 *
 * @deprecated use regular Button element instead
 */
const GradientButton = ({
  gradient = EColorGradients.Sunrise,
  borderWidth = '2px',
  borderStyle = 'solid',
  dashedColor = 'var(--gray-800)',
  width,
  height,
  minWidth,
  minHeight,
  selected = true,
  children = <></>,
  wrapperClassNames = '',
  buttonClassNames = '',
  value,
  alignment = 'center',
  loading = false,
  buttonProps = {},
  fullBackgroundGradient = false,
  adminButton,
  color,
  asLink,
  linkProps = { to: '' },
  opacity,
  hoverOpacity,
  margin,
  inline,
  wrapperProps = {},
  defaultBackground = 'var(--gray-800)',
  unselectedHoverBackground,
  selectedHoverBackground,
  hoverBoxShadow,
  padding,
  outlineOnFocus,
  fillUnselected,
  transitionDuration,
}: IGradientButtonProps) => {
  const { disabled, ...restButtonProps } = buttonProps;

  return (
    <div
      className={classNames(
        'gradient-button-wrapper',
        `align-${alignment}`,
        {
          selected,
          disabled,
          adminButton,
          fillUnselected,
        },
        wrapperClassNames
      )}
      style={{
        padding: borderWidth,
        ...(selected
          ? {
              border: `${borderWidth} ${borderStyle} var(${gradient})`,
              background: `var(${gradient})`,
            }
          : {
              '--gradient-btn-default-background': defaultBackground,
            }),
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        ...(minWidth ? { minWidth } : {}),
        ...(minHeight ? { minHeight } : {}),
        ...(inline ? { display: 'inline-flex' } : {}),
        ...(margin || margin === 0 ? { margin } : {}),
        ...(opacity || opacity === 0 ? { '--gradient-button-opacity': opacity } : {}),
        ...(hoverOpacity || hoverOpacity === 0 ? { '--gradient-button-hover-opacity': hoverOpacity } : {}),
        ...(hoverBoxShadow ? { '--gradient-btn-hover-box-shadow': hoverBoxShadow } : {}),
        ...(transitionDuration ? { '--gradient-btn-transition-duration': transitionDuration } : {}),
        ...(unselectedHoverBackground
          ? { '--gradient-btn-unselected-hover-background': unselectedHoverBackground }
          : {}),
        ...(selectedHoverBackground ? { '--gradient-btn-selected-hover-background': selectedHoverBackground } : {}),
        ...(padding ? { '--gradient-btn-padding': padding } : {}),
      }}
      {...wrapperProps}
    >
      <OptionalComponent display={borderStyle === 'dashed'}>
        <div
          className="dashed-border"
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            border: `1px dashed ${dashedColor}`,
            borderRadius: 'inherit',
            overflow: 'hidden',
          }}
        />
      </OptionalComponent>
      {asLink ? (
        <Link
          className={classNames('clear-button', buttonClassNames, {
            'full-background-gradient': fullBackgroundGradient,
          })}
          style={{
            ...(color ? { color } : {}),
          }}
          {...linkProps}
        >
          <div
            className="gradient-link-content-wrapper"
            style={{
              ...(color ? { color } : {}),
            }}
          >
            {children}
          </div>
        </Link>
      ) : (
        <button
          className={classNames('clear-button', buttonClassNames, {
            'full-background-gradient': fullBackgroundGradient,
            'outline-on-focus': outlineOnFocus,
          })}
          value={value}
          disabled={disabled}
          style={{
            ...(color ? { color } : {}),
          }}
          {...restButtonProps}
        >
          <>
            {children}
            {loading ? (
              <div className="gradient-button-loading-overlay">
                <WaitingIndicator />
              </div>
            ) : null}
          </>
        </button>
      )}
    </div>
  );
};

export default GradientButton;
